/**
 * Wrapper around the imageCompressor Web Worker.
 *
 * Single shared worker instance per browser tab; compresses many files in parallel
 * but each call awaits its own result via a message id.
 *
 * Usage:
 *   const compressed = await compressImage(file);
 *   // -> File or Blob (smaller WebP) OR the original file if not eligible
 */

let workerInstance = null;
let nextId = 1;
const pending = new Map();

function getWorker() {
  if (workerInstance) return workerInstance;
  try {
    // CRA + webpack 5 syntax for ES module workers
    workerInstance = new Worker(
      new URL('../workers/imageCompressor.worker.js', import.meta.url),
      { type: 'module' }
    );
    workerInstance.onmessage = (e) => {
      const { type, id } = e.data || {};
      const handler = pending.get(id);
      if (!handler) return;
      pending.delete(id);
      if (type === 'done') handler.resolve({ ok: true, ...e.data });
      else if (type === 'skip') handler.resolve({ ok: false, ...e.data });
      else if (type === 'error') handler.resolve({ ok: false, ...e.data });
    };
    workerInstance.onerror = () => {
      // If worker dies, reject everything pending and force fallback
      for (const [, h] of pending) h.resolve({ ok: false, reason: 'worker_error' });
      pending.clear();
      workerInstance = null;
    };
    return workerInstance;
  } catch (e) {
    return null;
  }
}

/**
 * Compress an image. Returns:
 *   { ok: true, blob: Blob, originalSize, newSize, mime }       — compression succeeded
 *   { ok: false, reason: 'not_image'|'too_small'|'no_gain'|... } — caller should upload original
 *
 * The caller should NEVER throw on error; it always returns an object.
 */
export async function compressImage(file, opts = {}) {
  // Quick sanity bypass for non-image files
  if (!file || !file.type || !file.type.startsWith('image/')) {
    return { ok: false, reason: 'not_image' };
  }
  const w = getWorker();
  if (!w) return { ok: false, reason: 'no_worker_support' };

  const id = nextId++;
  return new Promise((resolve) => {
    pending.set(id, { resolve });
    w.postMessage({
      type: 'compress',
      id,
      file,
      maxDim: opts.maxDim ?? 4096,
      quality: opts.quality ?? 0.85,
    });
    // Safety timeout: 60s per image
    setTimeout(() => {
      if (pending.has(id)) {
        pending.delete(id);
        resolve({ ok: false, reason: 'timeout' });
      }
    }, 60000);
  });
}

/**
 * If compression succeeded, returns a new File with a `.webp` extension
 * (or `.jpg` if WebP wasn't supported). Otherwise returns the original file.
 */
export async function compressImageToFile(file, opts = {}) {
  const result = await compressImage(file, opts);
  if (!result.ok) return file;
  const base = (file.name || 'image').replace(/\.[^.]+$/, '');
  const ext = result.mime === 'image/jpeg' ? 'jpg' : 'webp';
  const newName = `${base}.${ext}`;
  // File constructor preserves type/lastModified
  try {
    return new File([result.blob], newName, {
      type: result.mime,
      lastModified: file.lastModified || Date.now(),
    });
  } catch {
    return result.blob;
  }
}
